import { computed, inject, Injectable, signal } from '@angular/core';
import { ApiErrorResponse, ErrorHandlerV2Service } from '@gea/digital-ui-lib';
import { catchError, of, tap } from 'rxjs';

import { SystemHealthApiService } from '@shared/services';
import { GetHealthCheckParams, SystemHealthModel } from '@shared/models';

export const defaultSystemHealthState: SystemHealthModel = {
  isLoading: false,
};

/** Signal-based store for system health checks and the selected machine. */
@Injectable({ providedIn: 'root' })
export class SystemHealthStore {
  private readonly systemHealthApi = inject(SystemHealthApiService);
  private readonly errorHandler = inject(ErrorHandlerV2Service);
  private readonly state = signal<SystemHealthModel>(defaultSystemHealthState);

  readonly isLoading = computed(() => this.state().isLoading);
  readonly machine = computed(() => this.state().machine);
  readonly selectedMachine = computed(() => this.state().selectedMachine);
  readonly lastHealthCheckTime = computed(() => this.state().lastHealthCheckTime);
  readonly components = computed(() => this.state().machine?.components);
  readonly machineStatus = computed(() => this.state().machine?.status);

  setSelectedMachine(selectedMachine: number): void {
    this.patch({ selectedMachine });
    this.load({ machine: selectedMachine });
  }

  reset(): void {
    this.patch({ selectedMachine: undefined });
  }

  load({ machine, dateRange }: GetHealthCheckParams): void {
    this.patch({ isLoading: true });

    this.systemHealthApi
      .getHealthCheck(machine, dateRange)
      .pipe(
        catchError((error: ApiErrorResponse) => {
          if (error?.headers) {
            this.errorHandler.handleError(error);
          }
          return of<Partial<SystemHealthModel>>({});
        }),
        tap((systemHealth) => this.patch({ ...systemHealth, isLoading: false }))
      )
      .subscribe();
  }

  private patch(partial: Partial<SystemHealthModel>): void {
    this.state.update((current) => ({ ...current, ...partial }));
  }
}
